import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getLocale } from 'umi';
import { Checkbox } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon from '@op/components/BubbleCommon';
import EventMetricTrendEventSettingModal from '@/pages/dashboard/components/EventMetricTrend/EventMetricTrendEventSettingModal';
import { setBtnPointLog } from '@/pages/dashboard/utils/utils';
import type { IEventSettingConfirmPayload } from '@/pages/dashboard/components/EventMetricTrend/types';
import {
  KPI_EVENT_FILTER_LANGUAGE_DEFAULTS,
  type IKpiEventFilterLanguage,
} from './kpiEventFilterLanguageDefaults';
import './index.scss';

export type { IKpiEventFilterLanguage } from './kpiEventFilterLanguageDefaults';

/** 将配置中的 `\n` 换行渲染为与原先 JSX 一致的多行气泡 */
function multilineTips(text: string): JSX.Element {
  const lines = text.split('\n');
  return (
    <span>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <br /> : null}
          {line}
        </React.Fragment>
      ))}
    </span>
  );
}

/** 事件设置状态 */
export interface IChartEventSetting {
  showEventAxis: boolean;
  showEventBubbles: boolean;
  bubbleEvent: Record<string, unknown[]>;
  /** 图表浮窗内实验类是否平铺；默认 false */
  showExperimentEventDetailFlat?: boolean;
}

/** KpiEventFilter Props */
interface IKpiEventFilterProps {
  /** 当前事件设置状态 */
  chartEventSetting: IChartEventSetting;
  /** 事件设置变更回调 */
  changeEventSetting: (setting: IChartEventSetting) => void;
  /** 事件弹窗数据：按 event_type 分组的事件列表 */
  eventModalData?: Record<string, unknown[]>;
  /** 时间格式（传给弹窗） */
  eventTimeFormat?: string;
  /** 日期类型（传给弹窗） */
  eventDateType?: 'daily' | 'weekly' | 'monthly' | 'hourly';
  /** X 轴数据（传给弹窗） */
  eventXAxisData?: string[];
  /** 事件列表接口加载中（弹窗内 Abnormal 加载态） */
  eventModalLoading?: boolean;
  /** 事件列表接口失败（弹窗内 Abnormal 失败态） */
  eventModalError?: boolean;
  /** 自定义类名 */
  className?: string;
  /**
   * 用户是否已在弹窗点过「确定」写入 bubbleEvent（Overview 全屏用于区分未配置 vs 全不选）。
   */
  bubbleSelectionCommitted?: boolean;
  /**
   * 页面配置覆盖的中英文展示文案（与 pageConfig.filters[].kpiEventFilterLanguage 对应）；
   * 非「当前语言」枚举，内含 zh/en 两套字符串；未传字段用内置默认
   */
  language?: Partial<IKpiEventFilterLanguage>;
}

/**
 * KPI 概览事件筛选器
 *
 * 独立于 Filter 筛选器模块，不受确认/重置按钮影响。
 * 包含：Checkbox 开关、设置图标（点击打开事件设置弹窗）、两组提示气泡。
 * 参考舆情 MetricsChart 的事件设置交互封装。
 */
const KpiEventFilter: React.FC<IKpiEventFilterProps> = (props) => {
  const {
    chartEventSetting,
    changeEventSetting,
    eventModalData,
    eventTimeFormat = 'YYYY-MM-DD',
    eventDateType = 'daily',
    eventXAxisData = [],
    eventModalLoading = false,
    eventModalError = false,
    className,
    language: languageProp,
    bubbleSelectionCommitted = false,
  } = props;

  const mergedLanguage = useMemo(
    () => ({
      ...KPI_EVENT_FILTER_LANGUAGE_DEFAULTS,
      ...languageProp,
    }),
    [languageProp],
  );

  const checkboxLabel =
    getLocale() === 'zh-CN'
      ? mergedLanguage.checkboxLabelZh
      : mergedLanguage.checkboxLabelEn;

  const primaryTipsMap = useMemo(
    () => ({
      en: multilineTips(mergedLanguage.primaryTipsEn),
      zh: multilineTips(mergedLanguage.primaryTipsZh),
    }),
    [mergedLanguage.primaryTipsEn, mergedLanguage.primaryTipsZh],
  );

  const secondaryTipsMap = useMemo(
    () => ({
      en: mergedLanguage.secondaryTipsEn,
      zh: mergedLanguage.secondaryTipsZh,
    }),
    [mergedLanguage.secondaryTipsEn, mergedLanguage.secondaryTipsZh],
  );

  // 事件弹窗显隐状态
  const [transVisible, setTransVisible] = useState(false);

  /**
   * 勾选「显示事件」会向上冒泡并重算 KPI 多卡（多张 echarts setOption）+ Context，
   * 受控 Checkbox 若只跟 props，要等整棵树算完才变，体感卡顿。这里维护本地乐观值；
   * 并用 setTimeout(0) 把父级重渲染放到下一个 macrotask，让浏览器先 paint 勾选框
   * 新状态，再执行重活。嵌套 rAF 在主线程繁忙时会被合并帧，不够稳。
   */
  const [eventMasterQuick, setEventMasterQuick] = useState<boolean | null>(
    null,
  );

  const derivedEventMasterEnabled =
    chartEventSetting.showEventAxis || chartEventSetting.showEventBubbles;

  useEffect(() => {
    if (eventMasterQuick === null) {
      return;
    }
    if (derivedEventMasterEnabled === eventMasterQuick) {
      setEventMasterQuick(null);
    }
  }, [derivedEventMasterEnabled, eventMasterQuick]);

  const checkboxEventMasterValue =
    eventMasterQuick !== null ? eventMasterQuick : derivedEventMasterEnabled;

  // ===== 事件处理函数 =====

  /** Checkbox 切换：联动 showEventAxis / showEventBubbles */
  const handleEventSettingChange = useCallback(
    (value: boolean) => {
      setEventMasterQuick(value);
      const nextSetting: IChartEventSetting = {
        showEventAxis: value,
        showEventBubbles: value,
        bubbleEvent: chartEventSetting.bubbleEvent,
        showExperimentEventDetailFlat:
          chartEventSetting.showExperimentEventDetailFlat ?? false,
      };
      // 切到新的 macrotask：浏览器在两个 task 之间会 paint 一次，勾选框先响应
      setTimeout(() => {
        changeEventSetting(nextSetting);
      }, 0);
    },
    [
      changeEventSetting,
      chartEventSetting.bubbleEvent,
      chartEventSetting.showExperimentEventDetailFlat,
    ],
  );

  /** 弹窗确认：同步选中状态与气泡事件到外部 */
  const handleEventSettingConfirm = useCallback(
    (payload: IEventSettingConfirmPayload) => {
      setTransVisible(false);
      changeEventSetting({
        bubbleEvent: payload.bubbleEvent as Record<string, unknown[]>,
        showEventAxis: payload.showEventAxis,
        showEventBubbles: payload.showEventBubbles,
        showExperimentEventDetailFlat: payload.showExperimentEventDetailFlat,
      });
    },
    [changeEventSetting],
  );

  /** 设置图标点击：打点 + 打开弹窗 */
  const handleEventSettingIconClick = useCallback(() => {
    setTransVisible(true);
    setBtnPointLog({ btnId: 'KPI_EventSetting' }, undefined);
  }, []);

  return (
    <div className={`dbd-events-setting${className ? ` ${className}` : ''}`}>
      {/* 主区域：Checkbox + 设置图标 */}
      <BubbleCommon showIcon={false} tipsMap={primaryTipsMap}>
        <div className="dbd-events-setting-inner">
          <Checkbox
            value={checkboxEventMasterValue}
            onChange={handleEventSettingChange}
          >
            <span>{checkboxLabel}</span>
          </Checkbox>
          <span
            className="dbd-events-setting-icon"
            onClick={handleEventSettingIconClick}
          >
            <SvgIcon type="jichushezhi" />
          </span>
        </div>
      </BubbleCommon>

      {/* 辅助提示气泡 */}
      <BubbleCommon
        placement="top"
        childStyle={{ verticalAlign: 'middle' }}
        tipsMap={secondaryTipsMap}
      />

      {/**
       * 事件设置弹窗（独立渲染在 Filter 外部）。
       * 始终挂载、仅用 visible 切换显隐：避免 `{transVisible && <Modal />}` 卸载导致
       * 弹窗内部 useSyncState 在每次重新打开时被重置为「默认全选」，
       * 进而覆盖用户上一次「取消勾选 + 确定」的选择。
       */}
      <EventMetricTrendEventSettingModal
        visible={transVisible}
        close={() => setTransVisible(false)}
        confirm={handleEventSettingConfirm}
        data={eventModalData || {}}
        timeFormat={eventTimeFormat}
        showEventAxisValue={chartEventSetting.showEventAxis}
        showEventBubblesValue={chartEventSetting.showEventBubbles}
        showExperimentEventDetailFlatValue={
          chartEventSetting.showExperimentEventDetailFlat ?? false
        }
        dateType={eventDateType}
        xAxisData={eventXAxisData}
        requestLoading={eventModalLoading}
        requestError={eventModalError}
        bubbleEventValue={chartEventSetting.bubbleEvent}
        bubbleSelectionCommitted={bubbleSelectionCommitted}
      />
    </div>
  );
};

export default KpiEventFilter;
